/**
 * MarketListPage — admin markets CRUD (P1 Tier-A, marketplace.market.manage).
 * AdminDataTable (kit) + a "New" button + a row-activated edit dialog + a
 * soft-delete confirm. The list endpoint filters by city only (no `sort`
 * param — verified against `admin_router.py:list_markets`), so both the
 * search box AND the column-header sort (SCRUM-159) operate client-side over
 * whatever pages are currently loaded, mirroring the existing search-box
 * comment below. Loading/empty/error states come from AdminDataTable (kit).
 * Soft-delete is the DELETE verb (the backend marks deleted_at; rows
 * disappear after invalidation). lat/lng are Decimal strings — not surfaced
 * in the table (edited in the dialog).
 *
 * The "Entrances" column (SCRUM-16x) was replaced with an Edit/Delete actions
 * column — `entrance_count` wasn't meaningful to an admin here and nothing
 * downstream reads it. Delete opens the same confirm BottomSheet the dialog's
 * own footer Delete button uses (`MarketFormDialog`'s `onRequestDelete`) —
 * there is exactly one delete-confirm flow, reachable two ways.
 */
import { useMemo, useState, type ReactElement } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Pencil, Trash2 } from "lucide-react";

import { BottomSheet, Button } from "@garden/ui";
import { ADMIN_NS, registerAdminI18n } from "@/features/admin/api";
import { AdminDataTable, type DataTableColumn, type SortState } from "@/features/admin/_shared";
import { useMarkets, useDeleteMarket } from "@/features/admin/markets/hooks";
import { MarketFormDialog } from "@/features/admin/markets/MarketFormDialog";
import type { AdminMarket } from "@/features/admin/markets/api";

const ICON_BUTTON_CLASS =
  "grid h-9 w-9 place-items-center rounded-full text-[var(--g-secondary)] hover:bg-[var(--g-elevated)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--g-primary)]";

/** Column key -> sort accessor. Strings compare via `localeCompare` (ru/ky/en
 * collation, case-insensitive). */
const SORT_ACCESSORS: Record<string, (m: AdminMarket) => string | number> = {
  name: (m) => m.name_ru,
  city: (m) => m.city,
  slug: (m) => m.slug,
};

function sortMarkets(rows: AdminMarket[], sort: SortState | null): AdminMarket[] {
  if (!sort) return rows;
  const accessor = SORT_ACCESSORS[sort.key];
  if (!accessor) return rows;
  const dir = sort.direction === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    if (typeof av === "number" && typeof bv === "number") return (av - bv) * dir;
    return String(av).localeCompare(String(bv), undefined, { sensitivity: "base" }) * dir;
  });
}

export function MarketListPage(): ReactElement {
  registerAdminI18n();
  const { t } = useTranslation(ADMIN_NS);
  const { data, isLoading, isError, hasNextPage, isFetchingNextPage, fetchNextPage } = useMarkets();
  const del = useDeleteMarket();

  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<SortState | null>(null);
  const [formFor, setFormFor] = useState<AdminMarket | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [deleting, setDeleting] = useState<AdminMarket | null>(null);

  const all: AdminMarket[] = useMemo(() => data?.pages.flatMap((p) => p.markets) ?? [], [data]);
  const q = search.trim().toLowerCase();
  const filtered = useMemo(
    () =>
      q === ""
        ? all
        : all.filter(
            (m) =>
              m.name_ru.toLowerCase().includes(q) ||
              m.city.toLowerCase().includes(q) ||
              m.slug.toLowerCase().includes(q),
          ),
    [all, q],
  );
  const rows = useMemo(() => sortMarkets(filtered, sort), [filtered, sort]);

  const columns: DataTableColumn[] = [
    { key: "name", label: t("markets.columns.name"), sortable: true },
    { key: "city", label: t("markets.columns.city"), sortable: true },
    { key: "slug", label: t("markets.columns.slug"), sortable: true },
    { key: "actions", label: t("markets.columns.actions"), align: "right" as const },
  ];

  const renderCell = (m: AdminMarket, key: string): ReactElement | string => {
    switch (key) {
      case "name": return <span className="font-medium">{m.name_ru}</span>;
      case "city": return m.city;
      case "slug": return <span className="text-[var(--g-secondary)]">{m.slug}</span>;
      case "actions":
        return (
          <div className="flex items-center justify-end gap-1">
            <button
              type="button"
              data-testid={`market-edit-${m.id}`}
              aria-label={t("markets.action.edit")}
              onClick={(e) => { e.stopPropagation(); openEdit(m); }}
              className={ICON_BUTTON_CLASS}
            >
              <Pencil size={16} strokeWidth={1.75} aria-hidden />
            </button>
            <button
              type="button"
              data-testid={`market-delete-${m.id}`}
              aria-label={t("markets.delete.action")}
              onClick={(e) => { e.stopPropagation(); setDeleting(m); }}
              className={ICON_BUTTON_CLASS}
            >
              <Trash2 size={16} strokeWidth={1.75} aria-hidden />
            </button>
          </div>
        );
      default: return "";
    }
  };
  const renderMobileCard = (m: AdminMarket): ReactElement => (
    <div className="flex items-start justify-between gap-2">
      <div>
        <div className="font-medium">{m.name_ru}</div>
        <div className="mt-0.5 text-sm text-[var(--g-secondary)]">{m.city}</div>
        <div className="mt-0.5 text-xs text-[var(--g-secondary)]">{m.slug}</div>
      </div>
      {/* Desktop exposes Edit/Delete via the "actions" column; AdminDataTable's
          mobile card has no equivalent slot, so this row mirrors those same
          actions here (otherwise mobile admins could never reach Delete). */}
      <div className="flex items-center gap-1">
        <button
          type="button"
          data-testid={`market-edit-mobile-${m.id}`}
          aria-label={t("markets.action.edit")}
          onClick={(e) => { e.stopPropagation(); openEdit(m); }}
          className={ICON_BUTTON_CLASS}
        >
          <Pencil size={16} strokeWidth={1.75} aria-hidden />
        </button>
        <button
          type="button"
          data-testid={`market-delete-mobile-${m.id}`}
          aria-label={t("markets.delete.action")}
          onClick={(e) => { e.stopPropagation(); setDeleting(m); }}
          className={ICON_BUTTON_CLASS}
        >
          <Trash2 size={16} strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </div>
  );

  const openCreate = (): void => { setFormFor(null); setFormOpen(true); };
  const openEdit = (m: AdminMarket): void => { setFormFor(m); setFormOpen(true); };

  const confirmDelete = (): void => {
    if (!deleting) return;
    del.mutate(deleting.id, {
      onSuccess: () => { toast.success(t("markets.delete.toast")); setDeleting(null); setFormOpen(false); },
      onError: () => toast.error(t("error.generic")),
    });
  };

  return (
    <div data-testid="admin-markets-page" className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-2">
        <h1 className="text-xl font-bold">{t("markets.title")}</h1>
        <Button variant="primary" size="md" data-testid="admin-markets-new" onClick={openCreate} className="min-h-[44px]">
          {t("markets.new")}
        </Button>
      </div>

      <input
        type="search"
        data-testid="admin-markets-search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={t("markets.searchPlaceholder")}
        aria-label={t("common.search")}
        className="min-h-[44px] w-full max-w-md rounded-[var(--g-radius-sm)] border border-[var(--g-hairline)] bg-[var(--g-surface)] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--g-primary)]"
      />

      {/* No `onRowActivate`: Edit/Delete are explicit buttons in the "actions"
          column (and mirrored on the mobile card). A whole-row role="button"
          would nest focusable controls inside another focusable control —
          axe's nested-interactive rule (confirmed via MarketList.a11y.test). */}
      <AdminDataTable<AdminMarket>
        columns={columns}
        rows={rows}
        rowKey={(m) => m.id}
        renderCell={renderCell}
        renderMobileCard={renderMobileCard}
        rowAriaLabel={(m) => m.name_ru}
        testid="admin-markets"
        isLoading={isLoading}
        isError={isError}
        emptyLabel={t("markets.empty")}
        errorLabel={t("error.loadFailed")}
        sort={sort}
        onSortChange={setSort}
        sortAscLabel={t("kit.table.sortAsc")}
        sortDescLabel={t("kit.table.sortDesc")}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        onLoadMore={() => void fetchNextPage()}
        loadMoreLabel={t("common.loadMore")}
        loadingMoreLabel={t("common.refreshing")}
      />

      <MarketFormDialog
        key={formOpen ? (formFor?.id ?? "new") : "form-closed"}
        open={formOpen}
        market={formFor}
        onClose={() => setFormOpen(false)}
        onSuccess={() => setFormOpen(false)}
        onRequestDelete={(m) => setDeleting(m)}
      />

      <BottomSheet open={deleting !== null} onClose={() => setDeleting(null)} title={t("markets.delete.confirmTitle")}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[var(--g-secondary)]">{t("markets.delete.confirmBody")}</p>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setDeleting(null)} disabled={del.isPending} className="min-h-[44px]">{t("common.cancel")}</Button>
            <Button variant="destructive" size="sm" data-testid="market-delete-confirm" onClick={confirmDelete} loading={del.isPending} className="min-h-[44px]">{t("markets.delete.confirm")}</Button>
          </div>
        </div>
      </BottomSheet>
    </div>
  );
}

export default MarketListPage;
